import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
    FILTER_JOB_KEY,
    FILTER_WORK_CENTERS_KEY,
} from "../../../assets/constants";
import {
    JOB_ROLES,
    LOCATIONS,
    PRIMARY_JOB_ROLES,
    REPORTING_TO,
    SHIFT_PATTERNS,
    WORK_CENTERS,
} from "../../../components/filters/constants";
import {WORKER_TYPES} from "../../../components/filters/constants";
import { StoreType } from "../../../utils/types";
import { Filter } from "../../filters/filter-accordion/types";
import { AppliedFilter } from "../../filters/filter-context";
import {
    fetchJobsMatrixData,
    resetUpdateWorkerJobStatus,
    updateWorkerJobStatus,
} from "../store/jobs-matrix-slice";
import { JobsMatrixWorker, ManageWorkerJobPayload } from "../store/types";
import { JobStatus } from "../store/types";
import useJobsMatrixData from "./use-jobs-matrix-data";
import { useJobsMatrixFilters } from "./use-jobs-matrix-filters";

const getAllReportees = (managerIds: number[], allManagers: any[]): number[] => {
	const reportees = new Set(managerIds)
	const queue = [...managerIds]
	while (queue.length > 0) {
		const currentId = queue.shift()!
		allManagers
			.filter((m) => m.managerId === currentId)
			.forEach((m) => {
				if (!reportees.has(m.id)) {
					reportees.add(m.id)
					queue.push(m.id)
				}
			})
	}
	return Array.from(reportees)
}

export interface JobsMatrixControllerReturn {
    data: JobsMatrixWorker[];
    allJobs: any[];
    jobStatuses: JobStatus[];
    loading: boolean;
    searchText: string;
    updateWorkerJobStatusState: "idle" | "pending" | "fulfilled" | "rejected";
    updateWorkerJobStatusError: string | null;
    handleJobStatusChange: (params: any, jobId: number, statusCode: string, setState: React.Dispatch<React.SetStateAction<string | null>>) => void;
    handleSearchChange: (value: string) => void;
    handleResetUpdateStatus: () => void;
    filters: Filter[];
    setFilters: (newFilters: Filter[]) => void;
    appliedFilters: AppliedFilter[];
    isFilterOpen: boolean;
    setIsFilterOpen: React.Dispatch<React.SetStateAction<boolean>>;
    filterStatus: "idle" | "pending" | "fulfilled" | "error";
    filterAccordionHeight: number;
    defaultAppliedFilters: AppliedFilter[];
    isJobDetailsDialogOpen: boolean;
}

export const useJobsMatrixController = (): JobsMatrixControllerReturn => {
    const dispatch = useDispatch();
    const [searchText, setSearchText] = useState("");

    const { data, jobStatuses, loading, updateWorkerJobStatusState, updateWorkerJobStatusError } =
        useSelector((state: StoreType) => ({
            data: state.jobsMatrix?.data || [],
            filterData: state.jobsMatrix?.filterData || {},
            jobStatuses: state.jobsMatrix?.jobStatuses || [],
            loading: state.jobsMatrix?.loading || false,
            updateWorkerJobStatusState:
                state.jobsMatrix?.updateWorkerJobStatus || "idle",
            updateWorkerJobStatusError:
                state.jobsMatrix?.updateWorkerJobStatusError || null,
        }));

    const {
        filters,
        setFilters,
        appliedFilters,
        isFilterOpen,
        setIsFilterOpen,
        filterStatus,
        filterAccordionHeight,
        defaultAppliedFilters,
    } = useJobsMatrixFilters();

    const { filterData, isJobDetailsDialogOpen } = useJobsMatrixData();

    useEffect(() => {
        dispatch(fetchJobsMatrixData());
    }, [dispatch]);

    // Enhanced filtering with both search and applied filters
    const filteredData = useMemo(() => {
    // First, enrich the data with names from filter data
    let result = data.map((worker: JobsMatrixWorker) => {
        const location = filterData?.allLocations?.find((loc: any) => loc.id === worker.locationId);
        const workcenter = filterData?.allWorkcenters?.find((wc: any) => wc.id === worker.workcenterId);
        const workerType = filterData?.workerTypes?.find((type: any) => type.id === worker.workerTypeId);
        const manager = filterData?.allManagers?.find((mgr: any) => mgr.id === worker.managerId);
        const primaryJob = filterData?.allJobRoles?.find((job: any) => job.id === worker.primaryJobId);

        return {
            ...worker,
            locationName: location?.name || "",
            workcenterName: workcenter?.name || "",
            workerTypeName: workerType?.name || "",
            managerName: manager?.name || "",
            primaryJobName: primaryJob?.name || "",
        };
    });
    let filteredJobs = filterData?.allJobRoles || [];

    // Apply filter accordion filters first
    if (appliedFilters.length > 0) {
        result = result.filter((worker: JobsMatrixWorker) => {
            return appliedFilters.every((filter) => {
                if (!filter?.data?.length) return true;

                switch (filter.type) {
                    case WORKER_TYPES: {
                        const selectedIds = new Set(filter.data.map((item: any) => Number(item.id)));
                        const workerTypeId = Number(worker.workerTypeId);
                        const subtypeId = worker.workerSubTypeId ? Number(worker.workerSubTypeId) + 1000 : null;
                        
                        const hasSubtypeFilters = Array.from(selectedIds).some(id => id >= 1000);
                        
                        if (selectedIds.has(workerTypeId)) {
                            return !hasSubtypeFilters || (subtypeId && selectedIds.has(subtypeId));
                        }
                        
                        return false;
                    }
                    case LOCATIONS:
                        return filter.data.some((location: any) => location?.id === worker.locationId);
                    case WORK_CENTERS:
                        return filter.data.some((workCenter: any) => workCenter?.id === worker.workcenterId);
                    case PRIMARY_JOB_ROLES:
                        return filter.data.some((job: any) => job?.id === worker.primaryJobId);
                    case REPORTING_TO: {
                        const selectedIds = filter.data.map((manager: any) => manager.id)
                        const expandedIds = getAllReportees(selectedIds, filterData?.allManagers ?? [])
                        return expandedIds.includes(worker.managerId)
                    }
                    case SHIFT_PATTERNS:
                        return filter.data.some((pattern: any) => pattern?.id === worker.shiftPatternId);
                    default:
                        return true;
                }
            });
        });

        // Filter jobs
        filteredJobs = filteredJobs.filter((job: any) => {
            return appliedFilters.every((filter) => {
                if (!filter?.data?.length) return true;

                switch (filter.type) {
                    case JOB_ROLES:
                        return filter.data.some((jobFilter: any) => jobFilter?.id === job.id);
                    case LOCATIONS: {
                        const wcJobRelation = filterData?.filterRelationships?.find(
                            (r: any) => r.from === FILTER_WORK_CENTERS_KEY && r.to === FILTER_JOB_KEY
                        );
                        if (!wcJobRelation) return true;
                        return filter.data.some((location: any) => {
                            const workCentersInLocation = filterData?.allWorkcenters?.filter(
                                (wc: any) => wc.locationId === location.id
                            );
                            return workCentersInLocation?.some((wc: any) => {
                                const mapping = wcJobRelation.mappings?.find(
                                    (m: any) => m.fromId === wc.id
                                );
                                return mapping?.toIds?.includes(job.id);
                            });
                        });
                    }
                    case WORK_CENTERS: {
                        const wcJobRelation = filterData?.filterRelationships?.find(
                            (r: any) => r.from === FILTER_WORK_CENTERS_KEY && r.to === FILTER_JOB_KEY
                        );
                        if (!wcJobRelation) return true;
                        return filter.data.some((workCenter: any) => {
                            const mapping = wcJobRelation.mappings?.find(
                                (m: any) => m.fromId === workCenter.id
                            );
                            return mapping?.toIds?.includes(job.id);
                        });
                    }
                    default:
                        return true;
                }
            });
        });
    }

    // Then apply search filter across all columns using enriched data
    if (searchText.trim()) {
        const searchLower = searchText.toLowerCase().trim();
        result = result.filter((worker: any) => {
            const fullName = `${worker.firstName} ${worker.lastName}`.toLowerCase();
            const companyEmployeeId = worker.companyEmployeeId?.toLowerCase() ?? "";
            const jobTitle = worker.jobTitle?.toLowerCase() ?? "";
            const locationName = worker.locationName?.toLowerCase() ?? "";
            const workcenterName = worker.workcenterName?.toLowerCase() ?? "";
            const workerTypeName = worker.workerTypeName?.toLowerCase() ?? "";
            const managerName = worker.managerName?.toLowerCase() ?? "";
            const primaryJobName = worker.primaryJobName?.toLowerCase() ?? "";

            return (
                fullName.includes(searchLower) ||
                companyEmployeeId.includes(searchLower) ||
                jobTitle.includes(searchLower) ||
                locationName.includes(searchLower) ||
                workcenterName.includes(searchLower) ||
                workerTypeName.includes(searchLower) ||
                managerName.includes(searchLower) ||
                primaryJobName.includes(searchLower)
            );
        });
    }

    return { workers: result, jobs: filteredJobs };
}, [data, appliedFilters, searchText, filterData]);

    const handleJobStatusChange = useCallback(
        (
            params: any,
            jobId: number,
            statusCode: string,
            setState: React.Dispatch<React.SetStateAction<string | null>>
        ) => {
            const payload: ManageWorkerJobPayload = {
                workerId: params.row.workerId,
                jobs: [
                    {
                        jobId,
                        statusCode,
                    },
                ],
            };

            (dispatch(updateWorkerJobStatus(payload)) as any)
                .unwrap()
                .then(() => {
                    // Update local state on success
                    setState(statusCode);
                })
                .catch(() => {
                    // On error, keep the previous state
                });
        },
        [dispatch]
    );

    const handleSearchChange = useCallback((value: string) => {
        setSearchText(value);
    }, []);

    const handleResetUpdateStatus = useCallback(() => {
        dispatch(resetUpdateWorkerJobStatus());
    }, [dispatch]);

    return {
        data: filteredData?.workers,
        allJobs: filteredData?.jobs,
        jobStatuses,
        loading,
        searchText,
        updateWorkerJobStatusState,
        updateWorkerJobStatusError,
        handleJobStatusChange,
        handleSearchChange,
        handleResetUpdateStatus,
        // Filter-related
        filters,
        setFilters,
        appliedFilters,
        isFilterOpen,
        setIsFilterOpen,
        filterStatus,
        filterAccordionHeight,
        defaultAppliedFilters,
        isJobDetailsDialogOpen,
    };
};
